import { Injectable } from '@angular/core';

import {
  filter,
  firstValueFrom,
  map,
  ReplaySubject,
  tap,
  withLatestFrom,
} from 'rxjs';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '@env/environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { ApplicationInsightsService } from 'app/application-insights.service';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  RedirectRequest,
  ServerError,
} from '@azure/msal-browser';
import {
  AuthErrorCodeType,
  BrowserAuthErrorCodeType,
} from '@auth/auth-error-code-type';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import { AppStatusService } from './app-status.service';
import { CacheService } from '@shared/services';
import { SortDirection } from '@shared/enums';

// We are only interested in the JWT Decoder, and are not interested in extended injectable features,
// so they recommend to simply create an instance of the utility and use it directly:

@UntilDestroy()
@Injectable()
export class AuthService {
  authenticationResult$ = new ReplaySubject<AuthenticationResult>();

  private isSigningOut = false;

  // this is necessary to get volatile data from the authentication result (the access token)
  // and manually update affected areas (SignalR service etc)
  _authResult = this.msalBroadcast.msalSubject$.pipe(
    untilDestroyed(this),
    filter((x) => x.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
    map((x) => x.payload as AuthenticationResult)
  );

  isSigningOut$ = this.msalBroadcast.msalSubject$.pipe(
    untilDestroyed(this),
    filter(
      (x: EventMessage) =>
        x.eventType === EventType.LOGOUT_START ||
        x.eventType === EventType.LOGOUT_END
    ),
    tap((event: EventMessage) => {
      switch (event.eventType) {
        case EventType.LOGOUT_START:
          this.isSigningOut = true;
          break;
        case EventType.LOGOUT_END:
        case EventType.LOGOUT_SUCCESS:
          this.isSigningOut = false;
          break;
        default:
          this.isSigningOut = false;
          break;
      }
    })
  );

  onUserRedirect$ = this.msalBroadcast.msalSubject$.pipe(
    untilDestroyed(this),
    filter((x) => x.eventType === EventType.HANDLE_REDIRECT_END)
  );

  appReInit$ = this.msalBroadcast.msalSubject$.pipe(
    untilDestroyed(this),
    filter(
      (x) =>
        x.eventType === EventType.INITIALIZE_END ||
        x.eventType === EventType.HANDLE_REDIRECT_END
    )
  );

  private config = environment.azureAdB2C;
  private readonly scopes = [
    ...new Set([
      ...environment.dprApi.scopes,
      ...environment.coreApi.scopes,
      ...environment.filesApi.scopes,
    ]),
  ];

  constructor(
    private authService: MsalService,
    private appInsightsService: ApplicationInsightsService,
    private msalBroadcast: MsalBroadcastService,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private appFacade: AppFacade,
    private appStatusService: AppStatusService,
    private cacheService: CacheService
  ) {
    this._authResult.subscribe(this.authenticationResult$);
    this.isSigningOut$.subscribe();
    this.handleUserRedirect();
    this.handleInternalErrorEvents();
  }

  get isSigningOutState(): boolean {
    return this.isSigningOut;
  }
  get activeAccount(): AccountInfo {
    return this.authService.instance.getActiveAccount();
  }

  async signinRedirect(): Promise<void> {
    await this.authService.instance.loginRedirect();
  }

  async signoutRedirect(): Promise<void> {
    await this.authService.instance.logoutRedirect();
  }

  getUserEmail(): string {
    return this.activeAccount?.idTokenClaims?.email?.toString();
  }

  async init(): Promise<void> {
    this.authService.instance.enableAccountStorageEvents();
    await this.authService.instance.initialize();

    const account = this.authService.instance.getActiveAccount();

    if (
      account != null &&
      account.environment !== this.config.b2cPolicies.authorityDomain
    ) {
      await this.signoutRedirect();
    }

    // set backup authentication result when the access token is refreshed
    this._authResult
      .pipe(
        untilDestroyed(this),
        tap((result) => {
          this.offlineFacade.setBackupAuthenticationResult(result);
        })
      )
      .subscribe();

    await this.handleAuthenticationResponse();
  }

  async getAuthenticationResult(): Promise<AuthenticationResult> {
    let response: AuthenticationResult;
    try {
      response = await this.authService.instance.acquireTokenSilent({
        scopes: this.scopes,
      });
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error?.errorCode === BrowserAuthErrorCodeType.NoAccountError
      ) {
        return null;
      }

      if (error instanceof InteractionRequiredAuthError) {
        await this.authService.instance.acquireTokenRedirect({
          scopes: this.scopes,
        });
        response = await this.authService.instance.acquireTokenSilent({
          scopes: this.scopes,
        });
      } else {
        throw error;
      }
    }
    this.offlineFacade.setBackupAuthenticationResult(response);
    return response;
  }

  //#region events

  private async handleAuthenticationResponse(): Promise<void> {
    try {
      const response = this.appStatusService.online
        ? await this.getAuthResult()
        : await this.getPreviousAuthResult();

      this.authenticationResult$.next(response);

      await this.checkAndSetActiveAccount();
    } catch (error) {
      await this.handleAuthenticationError(error as ServerError);
    }
  }

  // if in offline mode, get the previous auth result to validate identity
  private async getPreviousAuthResult(): Promise<AuthenticationResult> {
    return await firstValueFrom(
      this.offlineFacade.query.backupAuthenticationResult$
    );
  }

  // if in online mode, get a new authentication result on init
  private async getAuthResult(): Promise<AuthenticationResult> {
    const response = await this.authService.instance.handleRedirectPromise();
    if (!response?.accessToken) {
      return await this.getAuthenticationResult();
    }
    this.offlineFacade.setBackupAuthenticationResult(response);
    return response;
  }

  private async handleAuthenticationError(error: ServerError): Promise<void> {
    // user clicked forget password while on login page
    if (error?.errorMessage?.indexOf(AuthErrorCodeType.ForgetPassword) > -1) {
      const userEmail = error.errorMessage.substring(
        error.errorMessage.lastIndexOf(':') + 2,
        error.errorMessage.length - 2
      );

      await this.resetPassword(userEmail);
    } else {
      this.appInsightsService.logException(error);

      await this.clearStorage();
    }
  }

  private async resetPassword(userEmail: string): Promise<void> {
    const request: RedirectRequest = {
      authority: this.config.b2cPolicies.authorities.passwordReset.authority,
      scopes: environment.dprApi.scopes,
      loginHint: userEmail,
    };
    await this.authService.instance.loginRedirect(request);
  }

  private async checkAndSetActiveAccount(): Promise<void> {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.activeAccount;
    const accounts = this.authService.instance.getAllAccounts();

    if (accounts.length === 0) {
      await this.authService.instance.loginRedirect();
      return;
    }

    if (accounts.length > 1) {
      await this.clearStorage();
      await this.authService.instance.loginRedirect();
      return;
    }

    if (!activeAccount) {
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private handleUserRedirect(): void {
    this.onUserRedirect$
      .pipe(
        withLatestFrom(
          this.userDetailsFacade.query.userDetails$,
          this.offlineFacade.query.dprDetailsList$
        ),
        tap(([_, userDetails, dprDetailsList]) => {
          this.offlineFacade.setBackupUserDetails(userDetails);
          this.offlineFacade.setBackupLastDprs(dprDetailsList);
        })
      )
      .subscribe();
  }

  private handleInternalErrorEvents(): void {
    this.msalBroadcast.msalSubject$
      .pipe(
        untilDestroyed(this),
        filter(
          (x) =>
            x.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
            x.eventType === EventType.SSO_SILENT_FAILURE
        ),
        tap(async (x) => {
          if (x.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            await this.authService.instance.ssoSilent({
              scopes: this.scopes,
              prompt: 'none',
            });
          }

          if (x.eventType === EventType.SSO_SILENT_FAILURE) {
            await this.authService.instance.acquireTokenRedirect({
              scopes: this.scopes,
            });
          }
        })
      )
      .subscribe();
  }

  private async clearStorage(): Promise<void> {
    // eslint-disable-next-line
    await this.authService.instance.clearCache();
    this.cacheService.cleanCache();
    this.appFacade.setAppLockedInOffline(false);
    this.appFacade.setDprsSortingDirection(SortDirection.Descending);
  }

  //#endregion events
}
